import { Component } from "react";

/**
 * Catches render errors in the page below it so the user sees a message (and the nav keeps working)
 * instead of React unmounting the whole app into a blank screen.
 * Keyed by pathname in Layout, so navigating to another page clears the error.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Page render error:", error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="page-error">
        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Something went wrong on this page</h1>
        <p className="text-sm text-slate-600 mb-6">
          The rest of the site still works. Reloading usually fixes it; if it keeps happening, please contact us and mention the page you were on.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button type="button" onClick={() => window.location.reload()} className="px-4 py-2 rounded-sm bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold" data-testid="page-error-reload">
            Reload page
          </button>
          <a href="/" className="px-4 py-2 rounded-sm border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50">
            Go to homepage
          </a>
        </div>
        {process.env.NODE_ENV !== "production" && (
          <pre className="mt-8 text-left text-xs bg-slate-100 p-3 rounded-sm overflow-x-auto">{String(this.state.error?.stack || this.state.error)}</pre>
        )}
      </div>
    );
  }
}
